import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Sparkles,
  LayoutDashboard,
  BarChart3,
  ListChecks,
  Sidebar,
  Bot,
  Table,
  Dna,
  CheckCircle,
  Trophy,
  CheckSquare
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { TourStep } from '@/hooks/useWelcomeTour';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  LayoutDashboard,
  BarChart3,
  ListChecks,
  Sidebar,
  Bot,
  Table,
  Dna,
  CheckCircle,
  Trophy,
  CheckSquare
};

interface TourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  targetElement: HTMLElement | null;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

export function TourTooltip({
  step,
  currentStep,
  totalSteps,
  targetElement,
  onNext,
  onPrev,
  onSkip,
  isFirst,
  isLast
}: TourTooltipProps) {
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    arrowPosition: 'top'
  });
  const isMobile = useIsMobile();

  const IconComponent = iconMap[step.icon] || Sparkles;

  useEffect(() => {
    if (!targetElement) {
      // Center position for modal-style steps
      setPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - (isMobile ? 160 : 180),
        arrowPosition: 'top'
      });
      return;
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const tooltipWidth = isMobile ? 320 : 360;
      const tooltipHeight = 220;
      const padding = isMobile ? 8 : 16;

      let top = 0;
      let left = 0;
      let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

      const preferredPosition = step.position || 'bottom';

      // Mobile: always position below or above, centered
      if (isMobile) {
        left = Math.max(padding, Math.min(
          window.innerWidth - tooltipWidth - padding,
          rect.left + rect.width / 2 - tooltipWidth / 2
        ));

        if (rect.bottom + tooltipHeight + padding < window.innerHeight) {
          top = rect.bottom + padding;
          arrowPosition = 'top';
        } else {
          top = Math.max(padding, rect.top - tooltipHeight - padding);
          arrowPosition = 'bottom';
        }
      } else {
        // Desktop positioning logic
        switch (preferredPosition) {
          case 'bottom':
            if (rect.bottom + tooltipHeight + padding < window.innerHeight) {
              top = rect.bottom + padding;
              left = rect.left + rect.width / 2 - tooltipWidth / 2;
              arrowPosition = 'top';
            } else {
              top = rect.top - tooltipHeight - padding;
              left = rect.left + rect.width / 2 - tooltipWidth / 2;
              arrowPosition = 'bottom';
            }
            break;
          case 'top':
            if (rect.top - tooltipHeight - padding > 0) {
              top = rect.top - tooltipHeight - padding;
              left = rect.left + rect.width / 2 - tooltipWidth / 2;
              arrowPosition = 'bottom';
            } else {
              top = rect.bottom + padding;
              left = rect.left + rect.width / 2 - tooltipWidth / 2;
              arrowPosition = 'top';
            }
            break;
          case 'right':
            if (rect.right + tooltipWidth + padding < window.innerWidth) {
              top = rect.top + rect.height / 2 - tooltipHeight / 2;
              left = rect.right + padding;
              arrowPosition = 'left';
            } else {
              top = rect.top + rect.height / 2 - tooltipHeight / 2;
              left = rect.left - tooltipWidth - padding;
              arrowPosition = 'right';
            }
            break;
          case 'left':
            if (rect.left - tooltipWidth - padding > 0) {
              top = rect.top + rect.height / 2 - tooltipHeight / 2;
              left = rect.left - tooltipWidth - padding;
              arrowPosition = 'right';
            } else {
              top = rect.top + rect.height / 2 - tooltipHeight / 2;
              left = rect.right + padding;
              arrowPosition = 'left';
            }
            break;
        }

        // Boundary checks
        left = Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, left));
        top = Math.max(padding, Math.min(window.innerHeight - tooltipHeight - padding, top));
      }

      setPosition({ top, left, arrowPosition });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [targetElement, step.position, isMobile]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 10 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="fixed z-[10000]"
        style={{
          top: position.top,
          left: position.left,
          width: isMobile ? 320 : 360
        }}
      >
        <Card className="shadow-2xl border-2 border-primary/20 bg-card">
          <CardHeader className={`${isMobile ? 'pb-2 pt-3 px-3' : 'pb-3'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg bg-primary/10`}>
                  <IconComponent className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-primary`} />
                </div>
                <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'}`}>{step.title}</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -mr-1 -mt-1"
                onClick={onSkip}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className={`${isMobile ? 'pt-0 px-3 pb-3' : 'pt-0'} space-y-4`}>
            <p className={`text-muted-foreground ${isMobile ? 'text-sm' : ''}`}>
              {step.description}
            </p>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-200 ${
                    i === currentStep 
                      ? `${isMobile ? 'w-5' : 'w-6'} h-2 bg-primary` 
                      : i < currentStep 
                        ? 'w-2 h-2 bg-primary/60' 
                        : 'w-2 h-2 bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size={isMobile ? "sm" : "default"}
                onClick={onSkip}
                className="text-muted-foreground"
              >
                Pular
              </Button>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <Button
                    variant="outline"
                    size={isMobile ? "sm" : "default"}
                    onClick={onPrev}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {!isMobile && "Anterior"}
                  </Button>
                )}

                <Button
                  size={isMobile ? "sm" : "default"}
                  onClick={onNext}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isLast ? (
                    <>
                      Concluir
                      <Trophy className="w-4 h-4 ml-1" />
                    </>
                  ) : (
                    <>
                      {!isMobile && "Próximo"}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
